import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import api from "../api";
import AuthLayout from "./AuthLayout";
import { isStrongPassword, PASSWORD_POLICY_MESSAGE } from "../utils/passwordPolicy";

const REGISTER_ENDPOINTS = ["/register", "/auth/register", "/tenant/register", "/users/register"];

const readServerText = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value?.message === "string") return value.message;
  return "";
};

const toFriendlyError = (error, fallback = "Unable to save account to database right now.") => {
  const status = error?.response?.status;
  const serverText = readServerText(error?.response?.data).toLowerCase();

  if (serverText.includes("method") && serverText.includes("does not exist")) {
    return "Signup API route is misconfigured on the backend. Please ask backend to fix the register controller method.";
  }
  if (serverText.includes("method not supported")) {
    return "Signup POST route is not enabled on the backend yet.";
  }
  if (status === 422) {
    return (
      error?.response?.data?.message ||
      "Validation failed. Please check your signup details and try again."
    );
  }
  if (status === 500) {
    return "Backend server error during signup. Please contact backend admin.";
  }
  if (status === 404) {
    return "Signup API route was not found on the backend.";
  }
  return error?.response?.data?.message || fallback;
};

function resolveRedirectTarget({ role, nextValue }) {
  if (role === "admin") {
    return "/home";
  }
  if (nextValue) {
    return nextValue;
  }
  return "/tenant/home";
}

export default function Signup({ onLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    address: "",
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const nextFromQuery = useMemo(() => searchParams.get("next") || "", [searchParams]);
  const nextFromState = location.state?.nextPath || "";
  const redirectTarget = nextFromQuery || nextFromState || "";

  const assignSession = ({ user, token }) => {
    localStorage.setItem("token", token);
    localStorage.setItem("email", user.email);
    localStorage.setItem("username", user.name || user.email || "Tenant");
    localStorage.setItem("role", user.role || "tenant");
    localStorage.setItem("loggedIn", "true");
    localStorage.setItem("tenant_has_linked_unit", "false");
    if (onLogin) {
      onLogin(user.role || "tenant");
    }
  };

  const handleSignup = async (event) => {
    event.preventDefault();
    setError("");
    if (!form.fullName.trim() || !form.email.trim() || !form.phone.trim() || !form.address.trim()) {
      setError("Please complete all required fields.");
      return;
    }
    if (!isStrongPassword(form.password)) {
      setError(PASSWORD_POLICY_MESSAGE);
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    const normalizedEmail = form.email.trim().toLowerCase();
    const payload = {
      name: form.fullName.trim(),
      full_name: form.fullName.trim(),
      email: normalizedEmail,
      phone: form.phone.trim(),
      contact_number: form.phone.trim(),
      address: form.address.trim(),
      password: form.password,
      confirm_password: form.confirmPassword,
      password_confirmation: form.confirmPassword,
      role: "tenant",
    };

    const completeLogin = ({ user, token }) => {
      assignSession({ user: { ...user, role: user.role || "tenant" }, token });
      navigate(resolveRedirectTarget({ role: user.role || "tenant", nextValue: redirectTarget }));
    };

    const tryApiLogin = async () => {
      const loginRes = await api.post("/login", {
        email: normalizedEmail,
        password: payload.password,
      });
      const loginData = loginRes.data || {};
      const loginUser = loginData.data?.user || loginData.user;
      const loginToken = loginData.data?.token || loginData.token;
      if (loginUser && loginToken) {
        completeLogin({ user: loginUser, token: loginToken });
        return true;
      }
      return false;
    };

    try {
      let registerRes = null;
      let lastError = null;

      for (const endpoint of REGISTER_ENDPOINTS) {
        try {
          registerRes = await api.post(endpoint, payload);
          break;
        } catch (endpointError) {
          lastError = endpointError;
        }
      }

      if (!registerRes) {
        throw lastError || new Error("No signup endpoint accepted the request.");
      }

      const registerData = registerRes.data || {};
      const user = registerData.data?.user || registerData.user;
      const token = registerData.data?.token || registerData.token;
      const failedStatus = registerData?.success === false || registerData?.status === "error";

      if (!failedStatus && user && token) {
        completeLogin({ user, token });
        return;
      }

      const loggedInFromApi = await tryApiLogin();
      if (loggedInFromApi) {
        return;
      }

      setError(
        "Account may have been created, but automatic login failed. Please try logging in manually."
      );
    } catch (apiError) {
      console.error(apiError);
      setError(toFriendlyError(apiError));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout wide title="Make yourself at home.">
          <div className="mb-8 border-b border-white/10 pb-6 text-center">
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-teal-300">
              The Vergara Rentals
            </p>
            <h1 className="mt-2 text-3xl font-bold text-white">Create Account</h1>
            <p className="mt-2 text-sm text-slate-400">
              Sign up to continue with your tenant application flow.
            </p>
          </div>

          <form onSubmit={handleSignup} className="grid gap-4 md:grid-cols-2">
            <label className="md:col-span-2 text-sm font-semibold text-slate-200">
              Full Name
              <input
                required
                value={form.fullName}
                onChange={(event) => setForm((prev) => ({ ...prev, fullName: event.target.value }))}
                className="mt-2 w-full rounded-lg border border-white/10 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
              />
            </label>

            <label className="text-sm font-semibold text-slate-200">
              Email Address
              <input
                type="email"
                required
                value={form.email}
                onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                className="mt-2 w-full rounded-lg border border-white/10 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
              />
            </label>

            <label className="text-sm font-semibold text-slate-200">
              Phone Number
              <input
                required
                value={form.phone}
                onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))}
                className="mt-2 w-full rounded-lg border border-white/10 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
              />
            </label>

            <label className="md:col-span-2 text-sm font-semibold text-slate-200">
              Address
              <input
                required
                value={form.address}
                onChange={(event) => setForm((prev) => ({ ...prev, address: event.target.value }))}
                className="mt-2 w-full rounded-lg border border-white/10 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
              />
            </label>

            <label className="text-sm font-semibold text-slate-200">
              Password
              <div className="relative mt-2">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={form.password}
                  onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
                  className="w-full rounded-lg border border-white/10 bg-white px-4 py-3 pr-14 text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
                />
                <button
                  type="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 inline-flex w-12 items-center justify-center text-slate-500 transition hover:text-slate-700"
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 3l18 18" />
                      <path d="M10.58 10.58a2 2 0 102.83 2.83" />
                      <path d="M16.24 16.24A10.8 10.8 0 0112 17c-5 0-9-5-9-5a17.3 17.3 0 014.06-4.79" />
                      <path d="M9.9 4.24A10.9 10.9 0 0112 4c5 0 9 5 9 5a17.2 17.2 0 01-2.22 3.11" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              <p className="mt-1 text-xs font-normal text-slate-400">{PASSWORD_POLICY_MESSAGE}</p>
            </label>

            <label className="text-sm font-semibold text-slate-200">
              Confirm Password
              <div className="relative mt-2">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  value={form.confirmPassword}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, confirmPassword: event.target.value }))
                  }
                  className="w-full rounded-lg border border-white/10 bg-white px-4 py-3 pr-14 text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
                />
                <button
                  type="button"
                  aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 inline-flex w-12 items-center justify-center text-slate-500 transition hover:text-slate-700"
                >
                  {showConfirmPassword ? (
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 3l18 18" />
                      <path d="M10.58 10.58a2 2 0 102.83 2.83" />
                      <path d="M16.24 16.24A10.8 10.8 0 0112 17c-5 0-9-5-9-5a17.3 17.3 0 014.06-4.79" />
                      <path d="M9.9 4.24A10.9 10.9 0 0112 4c5 0 9 5 9 5a17.2 17.2 0 01-2.22 3.11" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </label>

            <button
              type="submit"
              disabled={loading}
              className="md:col-span-2 rounded-lg bg-teal-600 py-3 text-lg font-semibold text-white transition hover:bg-teal-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? "Creating account..." : "Sign Up"}
            </button>
          </form>

          {error ? (
            <div className="mt-4 rounded-lg border border-red-300/30 bg-red-500/15 px-4 py-3 text-sm text-red-100">
              {error}
            </div>
          ) : null}

          <p className="mt-6 text-center text-sm text-slate-400">
            Already have an account?{" "}
            <Link
              to={redirectTarget ? `/login?next=${encodeURIComponent(redirectTarget)}` : "/login"}
              className="font-semibold text-teal-300 hover:text-teal-200"
            >
              Login
            </Link>
          </p>
    </AuthLayout>
  );
}
